import { IconStartTest } from '@doltech/icons-from-dev/IconStartTest';
import { IconFilesFileDocument } from '@doltech/icons/IconFilesFileDocument';
import { IconOtherQuestionnaire1 } from '@doltech/icons/IconOtherQuestionnaire1';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { DrawerModal } from '@doltech/ui/lib/figma/Mobile/Modal/DrawerModal';
import { ReadingText } from '@doltech/ui/lib/figma/Typography';
import { useDeviceDetect } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { ReadingViewModeEnum, zIndex } from '@doltech/utils/lib/constants';
import { scrollToQuestion } from '@doltech/utils/lib/scrollUtils';
import { isNil } from 'lodash';
import { observer } from 'mobx-react-lite';
import { getSnapshot } from 'mobx-state-tree';
import * as React from 'react';
import styled from 'styled-components';

import { DoTestFooter } from '../mobile/DoTest/Reading/Footer/DoTestFooter';
import { DoTestHeader } from '../mobile/DoTest/Reading/Header/DoTestHeader';
import { ReadingOverallDrawerModal } from '../mobile/Modal/ReadingOverallDrawerModal';
import { TestMobileSubmission } from '../mobile/TestMobileSubmission';
import { useSavedScrollPosition } from '../mobile/useSavedScrollPosition.hook';
import { useTestSubmissionStore } from '../model/test-submissions.hook';
import { ReadingGuidedTour } from './GuidedTour/ReadingGuidedTour';
import { ReadingTestSubmissionProps } from './ReadingTestSubmission';

const ItemNameSpan = styled.span`
  font-weight: 700;
`;

const DivRight = styled.div`
  position: fixed;
  bottom: 96px;
  display: flex;
  flex-flow: row-reverse;
  font-size: 24px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  justify-content: center;
  background: ${colorsV2.white100};
  right: 24px;
  align-items: center;
  box-shadow: 0 16px 32px rgba(45, 55, 66, 0.2);
  z-index: ${zIndex.FOOTER};
`;

interface DoTestReadingMobileLayoutProps extends ReadingTestSubmissionProps {
  onToggleViewMode?: () => void;
}

export const DoTestReadingMobileLayout = observer((props: DoTestReadingMobileLayoutProps) => {
  const { onSubmitWithEvents, disableSubmitAction, onToggleViewMode, children, onQuit } = props;
  const testSubmissionStore = useTestSubmissionStore();

  const currentSection = testSubmissionStore.currentTestSection;
  const currentViewMode = currentSection?.currentViewMode;
  const [showOverall, setShowOverall] = React.useState(false);
  const { isMobile } = useDeviceDetect();
  const { isPreviewMode } = testSubmissionStore;

  const toggleShowOverall = React.useCallback(() => {
    setShowOverall((prevShowOverall) => !prevShowOverall);
  }, []);

  useSavedScrollPosition({
    scrollContainerId: 'passage-container',
    trackedValue: testSubmissionStore.currentSectionIndex,
    deps: [currentViewMode, testSubmissionStore.tourGuide.isTourGuideFinished],
    scrollWhen: () =>
      currentViewMode === ReadingViewModeEnum.PASSAGE &&
      testSubmissionStore.tourGuide.isTourGuideFinished,
    updateScrollValueWhen: () => testSubmissionStore.tourGuide.isTourGuideFinished,
  });
  useSavedScrollPosition({
    scrollContainerId: 'body-container',
    trackedValue: testSubmissionStore?.currentSectionIndex || 0,
    deps: [currentViewMode, testSubmissionStore.tourGuide.isTourGuideFinished],
    scrollWhen: () =>
      currentViewMode === ReadingViewModeEnum.QUESTION &&
      testSubmissionStore.tourGuide.isTourGuideFinished,
    updateScrollValueWhen: () => testSubmissionStore.tourGuide.isTourGuideFinished,
  });

  const goToQuestion = (qUuid, testSectionIndex, gIndex) => {
    toggleShowOverall();
    testSubmissionStore.goToSectionIndex(testSectionIndex, false);
    testSubmissionStore.testSections[testSectionIndex].updateCurrentViewMode(
      ReadingViewModeEnum.QUESTION
    );
    testSubmissionStore.goToQuestionAtGroup(testSectionIndex, gIndex);
    // this magic use set time out to make sure question has been render after move to another test sections
    setTimeout(() => {
      scrollToQuestion(qUuid);
    });
  };
  const switchToPassageView = () => {
    if (currentViewMode === ReadingViewModeEnum.QUESTION) {
      onToggleViewMode();
      currentSection.updateCurrentViewMode(ReadingViewModeEnum.QUESTION);
    }
  };

  if (isNil(currentSection) || testSubmissionStore.initialed === false) return null;

  return (
    <TestMobileSubmission
      background={colorsV2.white100}
      onSubmit={onSubmitWithEvents}
      disableSubmitAction={disableSubmitAction}
      onSubmitWithEvents={onSubmitWithEvents}
      enableEvent
    >
      <DoTestHeader
        showQuitButton
        onQuit={onQuit}
        onSubmit={onSubmitWithEvents}
        totalPassage={getSnapshot(testSubmissionStore.testSections).length}
        testName={testSubmissionStore.name}
        testSectionName={currentSection.name}
      />
      {children}
      <DoTestFooter showOverall={toggleShowOverall} onNavigation={switchToPassageView} />
      {isMobile && (
        <DivRight
          className="btn-switch-view-mode"
          onClick={() => {
            onToggleViewMode();
          }}
        >
          {currentViewMode === ReadingViewModeEnum.PASSAGE ? (
            <IconOtherQuestionnaire1 />
          ) : (
            <IconFilesFileDocument />
          )}
        </DivRight>
      )}
      {!isPreviewMode && <ReadingGuidedTour setViewMode={currentSection.updateCurrentViewMode} />}
      {!isPreviewMode &&
        !testSubmissionStore.isStartedDoingTest &&
        testSubmissionStore.tourGuide.isTourGuideFinished && (
          <DrawerModal
            okButtonText="Start"
            maskClosable={false}
            onOk={testSubmissionStore.setStartDoingTest}
            visible
            title={<IconStartTest />}
          >
            <ReadingText>
              Chào mừng bạn đến với bài test <ItemNameSpan>{testSubmissionStore.name}</ItemNameSpan>
              <br />
              Bài test này có{' '}
              <ItemNameSpan>{testSubmissionStore?.testSections.length}</ItemNameSpan> passages
              <br />
              {testSubmissionStore?.testSections.map((testSection, index) => (
                <ReadingText key={index}>
                  - Passage <ItemNameSpan>{index + 1}</ItemNameSpan> có tổng cộng{' '}
                  <ItemNameSpan>{testSection.totalQuestionInGroup}</ItemNameSpan> câu hỏi
                  <br />
                </ReadingText>
              ))}
              Nếu bạn đã sẵn sàng, hãy bấm vào nút Start để bắt đầu
            </ReadingText>
          </DrawerModal>
        )}
      {showOverall && (
        <ReadingOverallDrawerModal
          onSubmit={() => {
            toggleShowOverall();
            onSubmitWithEvents();
          }}
          goToQuestion={goToQuestion}
          onClose={toggleShowOverall}
          visible={showOverall}
        />
      )}
    </TestMobileSubmission>
  );
});
